import { AllEnterpriseModule } from 'ag-grid-enterprise';
import ManagementTablePanel, { managementTableTheme } from '@/components/common/ManagementTablePanel';
import ColumnValuesFilter from '@/components/common/ColumnValuesFilter';
import { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  } from 'ag-grid-community';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { createTag, deleteTag, fetchTagCategories, fetchTags, type TagCategory, type TagItem, type TagPayload, updateTag } from '@/api/tags';
import { useI18n } from '@/i18n';
import { confirmToast } from '@/lib/notify';

const modules = [AllEnterpriseModule];

const theme = managementTableTheme;


function StatusBadge({ active }: { active: number }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        active === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
      }`}
    >
      {active === 1 ? 'Aktif' : 'Pasif'}
    </span>
  );
}

function ColorSwatch({ color }: { color: string }) {
  return (
    <div className="flex h-full items-center gap-2">
      <span className="h-4 w-4 rounded-full border border-slate-200" style={{ backgroundColor: color }} />
      <span className="text-xs font-mono text-slate-600">{color}</span>
    </div>
  );
}

interface TagModalState {
  mode: 'create' | 'edit';
  tag?: TagItem;
}

function TagModal({
  categories,
  state,
  onClose,
  onSaved,
}: {
  categories: TagCategory[];
  state: TagModalState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<TagPayload>({
    category_id: state.tag?.category_id ?? categories[0]?.id ?? 0,
    label: state.tag?.label ?? '',
    color: state.tag?.color ?? '#2563EB',
    is_active: state.tag?.is_active ?? 1,
  });

  function setField<Key extends keyof TagPayload>(key: Key, value: TagPayload[Key]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (!form.category_id || !form.label.trim() || !/^#[0-9a-fA-F]{6}$/.test(form.color)) {
      toast.error(t('management.tags.requiredError'));
      return;
    }

    setSaving(true);
    try {
      if (state.mode === 'create') {
        await createTag({ ...form, label: form.label.trim(), color: form.color.toUpperCase() });
        toast.success(t('management.tags.createSuccess'));
      } else {
        await updateTag(state.tag!.id, { ...form, label: form.label.trim(), color: form.color.toUpperCase() });
        toast.success(t('management.tags.updateSuccess'));
      }
      onSaved();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error(t('management.tags.duplicateError'));
      } else {
        toast.error(state.mode === 'create' ? t('management.tags.createError') : t('management.tags.updateError'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between border-b pb-3">
          <h3 className="text-lg font-semibold text-gray-900">
            {state.mode === 'create' ? t('management.tags.newTitle') : t('management.tags.editTitle')}
          </h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('management.tags.category')}</label>
            <select
              value={String(form.category_id)}
              onChange={(e) => setField('category_id', Number(e.target.value))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('management.tags.label')}</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setField('label', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('management.tags.color')}</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={form.color}
                onChange={(e) => setField('color', e.target.value.toUpperCase())}
                className="h-10 w-14 rounded-lg border border-gray-300 bg-white p-1"
              />
              <input
                type="text"
                value={form.color}
                onChange={(e) => setField('color', e.target.value)}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('common.status')}</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setField('is_active', 1)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  form.is_active === 1
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {t('management.users.active')}
              </button>
              <button
                type="button"
                onClick={() => setField('is_active', 0)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  form.is_active === 0
                    ? 'border-rose-500 bg-rose-50 text-rose-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {t('management.users.passive')}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3 border-t pt-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
          >
            {t('management.users.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save size={16} />
            {t('management.users.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionCell({
  data,
  onEdit,
  onDelete,
}: ICellRendererParams<TagItem> & {
  onEdit: (tag: TagItem) => void;
  onDelete: (tag: TagItem) => void;
}) {
  if (!data) return null;
  return (
    <div className="flex h-full items-center justify-center gap-2">
      <button
        onClick={(event) => {
          event.stopPropagation();
          onEdit(data);
        }}
        className="rounded-lg p-1.5 text-blue-600 transition hover:bg-blue-50"
        title="Düzenle"
      >
        <Pencil size={16} />
      </button>
      <button
        onClick={(event) => {
          event.stopPropagation();
          onDelete(data);
        }}
        className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50"
        title="Sil"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

export default function TagsTab() {
  const { t } = useI18n();
  const [categories, setCategories] = useState<TagCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const [rows, setRows] = useState<TagItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState<TagModalState | null>(null);

  useEffect(() => {
    fetchTagCategories()
      .then((items) => {
        setCategories(items);
        setSelectedCategoryId(items[0]?.id);
      })
      .catch(() => toast.error(t('management.tags.loadError')));
  }, [t]);

  useEffect(() => {
    if (!selectedCategoryId) return;
    setLoading(true);
    fetchTags(selectedCategoryId)
      .then(setRows)
      .catch(() => toast.error(t('management.tags.loadError')))
      .finally(() => setLoading(false));
  }, [selectedCategoryId, modalState, t]);

  const [search, setSearch] = useState('');

  const colDefs = useMemo<ColDef<TagItem>[]>(() => [
    { headerName: 'ID', field: 'id', width: 90, filter: ColumnValuesFilter },
    { headerName: t('management.tags.category'), field: 'category_name', minWidth: 150, flex: 1, filter: ColumnValuesFilter },
    { headerName: t('management.tags.label'), field: 'label', minWidth: 180, flex: 1.4, filter: ColumnValuesFilter },
    {
      headerName: t('management.tags.color'),
      field: 'color',
      minWidth: 150,
      cellRenderer: ({ value }: ICellRendererParams<TagItem>) => (value ? <ColorSwatch color={String(value)} /> : null),
    },
    {
      headerName: t('common.status'),
      field: 'is_active',
      width: 120,
        filterParams: { valueLabel: (value: unknown) => Number(value) === 1 ? 'Aktif' : 'Pasif' },
      cellRenderer: ({ value }: ICellRendererParams<TagItem>) => <StatusBadge active={Number(value ?? 0)} />,
    },
    { headerName: t('management.tags.createdOn'), field: 'created_on', minWidth: 160 },
    {
      headerName: t('management.users.actions'),
      field: 'id',
      width: 120,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<TagItem>) => (
        <ActionCell
          {...params}
          onEdit={(tag) => setModalState({ mode: 'edit', tag })}
          onDelete={async (tag) => {
            const confirmed = await confirmToast(t('management.tags.deleteConfirm', { name: tag.label }), t('management.tags.deleteConfirmMessage'));
            if (!confirmed) {
              return;
            }
            try {
              await deleteTag(tag.id);
              toast.success(t('management.tags.deleteSuccess'));
              setRows((prev) => prev.filter((item) => item.id !== tag.id));
            } catch {
              toast.error(t('management.tags.deleteError'));
            }
          }}
        />
      ),
    },
  ], [t]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <select
            value={selectedCategoryId ? String(selectedCategoryId) : ''}
            onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
            className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-600 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
          <div className="text-sm text-slate-500">{rows.length} etiket</div>
        </div>


      </div>

      <ManagementTablePanel search={search} onSearch={setSearch} label="Etiketlerde ara" count={rows.length} actions={<button
          type="button"
          onClick={() => setModalState({ mode: 'create' })}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
        >
          <Plus size={16} />
          Yeni
        </button>}>
        <div className="min-h-[300px] flex-1">
        <AgGridReact<TagItem>
          modules={modules}
          theme={theme}
          quickFilterText={search}
          pagination
          paginationPageSize={100}
          paginationPageSizeSelector={[25, 50, 100]}
          rowData={rows}
          columnDefs={colDefs}
          defaultColDef={{
            sortable: true,
            filter: ColumnValuesFilter,
            floatingFilter: false,
            resizable: true,
          }}
          loading={loading}
        />
        </div>
      </ManagementTablePanel>

      {modalState && (
        <TagModal
          categories={categories}
          state={modalState}
          onClose={() => setModalState(null)}
          onSaved={() => setModalState(null)}
        />
      )}
    </div>
  );
}
